import React from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
// Chakra imports
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useTheme,
} from '@chakra-ui/react';

import CheckEmailPNG from '../../assets/CheckEmail.png';

import DefaultLayout from './Layout';

import { FcGoogle } from 'react-icons/fc';
import { useAuth } from '../../Context/AuthContext';

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

function SignUp() {
  // Chakra color mode
  const textColor = useColorModeValue('#1B254B', 'white');
  const textColorSecondary = '#A0AEC0';
  const textColorDetails = useColorModeValue('#1B254B', '#A3AED0');
  const textColorBrand = useColorModeValue('#422AFB', 'white');
  const brandStars = useColorModeValue('#422AFB', '#7551FF');
  const googleBg = useColorModeValue('#F4F7FE', 'rgba(255, 255, 255, 0.08)');
  const googleText = useColorModeValue('#1B254B', 'white');
  const googleHover = useColorModeValue(
    { bg: '#E2E8F0' },
    { bg: 'rgba(255, 255, 255, 0.16)' }
  );
  const googleActive = useColorModeValue(
    { bg: '#F4F7FE' },
    { bg: 'rgba(255, 255, 255, 0.08)' }
  );
  const query = useQuery();
  const { login, signInWithGoogle, sendSignInLinkToEmailAuth } = useAuth();
  const emailRef = React.useRef();
  const history = useHistory();
  const [error, setError] = React.useState('');
  async function handleGoogleLoginClick(e) {
    e.preventDefault();

    try {
      setError('');
      e.target.disabled = true;
      const result = await signInWithGoogle();
      // The signed-in user info.

      const user = result.user;
      if (user) {
        const token = await user.getIdToken();
        localStorage.setItem('accessToken', token);
        history.push('/');
      }
    } catch {
      setError('Something went wrong! Please try again later');
      setTimeout(() => {
        setError('');
        e.target.disabled = false;
      }, 3000);
    }
  }

  const handleEmailVerificationLink = async e => {
    e.preventDefault();
    if (
      emailRef.current.value !== '' &&
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(emailRef.current.value)
    ) {
      // email is valid, send link
      try {
        console.log('sending link');
        e.target.disabled = true;
        localStorage.setItem('signInWithEmail', emailRef.current.value);
        await sendSignInLinkToEmailAuth(emailRef.current.value);
        history.push('/register?verify=true');
      } catch (error) {
        console.log(error);
        setError('Something went wrong! Please try again later');
        setTimeout(() => {
          setError('');
          e.target.disabled = false;
        }, 3000);
      }
    } else {
      setTimeout(() => {
        setError('');
      }, 3000);
      setError('Please enter a valid email');
    }
  };

  return (
    <DefaultLayout>
      <Box w="100%">
        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}
      </Box>
      {!query.get('verify') ? (
        <Flex
          maxW={{ base: '100%', md: 'max-content' }}
          w="100%"
          mx={{ base: 'auto', lg: '0px' }}
          me="auto"
          h="100%"
          alignItems="start"
          justifyContent="center"
          mb={{ base: '30px', md: '60px' }}
          px={{ base: '25px', md: '0px' }}
          mt={{ base: '40px', md: '14vh' }}
          flexDirection="column"
        >
          <Box me="auto">
            <Heading color={textColor} fontSize="36px" mb="10px">
              {query.get('reset') ? 'Reset Password' : 'Create an Account'}
            </Heading>
            <Text
              mb="36px"
              ms="4px"
              color={textColorSecondary}
              fontWeight="400"
              fontSize="md"
            >
              Enter your email to send verification link
            </Text>
          </Box>
          <Flex
            zIndex="2"
            direction="column"
            w={{ base: '100%', md: '420px' }}
            maxW="100%"
            background="transparent"
            borderRadius="15px"
            mx={{ base: 'auto', lg: 'unset' }}
            me="auto"
            mb={{ base: '20px', md: 'auto' }}
          >
            <FormControl display="flex" flexDirection="column">
              <FormLabel
                display="flex"
                direction="column"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Email<Text color={brandStars}>*</Text>
              </FormLabel>
              <Input
                isRequired={true}
                variant="auth"
                fontSize="sm"
                ms={{ base: '0px', md: '0px' }}
                type="email"
                placeholder="mail@gmail.com"
                mb="24px"
                fontWeight="500"
                size="lg"
                outline="1px solid #e0e5f2"
                ref={emailRef}
              />
              <Button
                fontSize="sm"
                variant="brand"
                fontWeight="500"
                w="100%"
                h="50"
                mb="24px"
                background="#422AFB"
                color="white"
                onClick={handleEmailVerificationLink}
              >
                Send Verification Link
              </Button>
            </FormControl>
            <Flex align="center" mb="25px">
              <Flex h="1px" w="100%" bg="rgba(135, 140, 189, 0.3)"></Flex>
              <Text color="#A0AEC0" mx="14px">
                or
              </Text>
              <Flex h="1px" w="100%" bg="rgba(135, 140, 189, 0.3)"></Flex>
            </Flex>
            <Button
              fontSize="sm"
              me="0px"
              mb="26px"
              py="15px"
              h="50px"
              borderRadius="16px"
              bg={googleBg}
              color={googleText}
              fontWeight="500"
              _hover={googleHover}
              _active={googleActive}
              _focus={googleActive}
              onClick={handleGoogleLoginClick}
            >
              <Icon as={FcGoogle} w="20px" h="20px" me="10px" />
              Sign in with Google
            </Button>

            <Flex
              flexDirection="column"
              justifyContent="center"
              alignItems="start"
              maxW="100%"
              mt="0px"
            >
              <Text color={textColorDetails} fontWeight="400" fontSize="14px">
                Already have an account?
                <NavLink to="/login">
                  <Text
                    color={textColorBrand}
                    as="span"
                    ms="5px"
                    fontWeight="500"
                  >
                    Sign in
                  </Text>
                </NavLink>
              </Text>
            </Flex>
          </Flex>
        </Flex>
      ) : (
        <Container mt="50px" textAlign="center">
          <Flex direction="column" alignItems="center">
            <Text fontSize="4xl" fontWeight="bold" mb="4">
              Verify Your Email
            </Text>

            <Box
              me="12px"
              mt="6px"
              mb="10px"
              width={{ base: '160px', xl: '200px', lg: '200px', md: '200px' }}
              height={{ base: '160px', xl: '200px', lg: '200px', md: '200px' }}
              color="#422AFB"
            >
              <img src={CheckEmailPNG} alt="Check Email" />
            </Box>
            <Text fontSize="lg" mb="4">
              Check your inbox for a verification email to complete your signup.
              If you don’t see it, make sure to check your spam folder.
            </Text>
            <Text fontSize="lg" mb="4">
              Need Help? Email us -
              <a href="mailto:mailrofficialai@gmail.com">
                mailrofficialai@gmail.com
              </a>
            </Text>
          </Flex>
        </Container>
      )}
    </DefaultLayout>
  );
}

export default SignUp;
